import {
  CustomerAction,
  AnnotationRecordStatus,
  HandId,
} from "@/entities/annotation";
import { ANNOTATION_TYPES } from "@/entities/annotation/model/consts";
import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "@/shared/api/base-query";
import { RelativeCoordinate } from "../types";
import { OrderWithJourneys } from "@/features/annotation/lib/helpers";
import { lodApiInstance } from "./axios";

const baseQuery = axiosBaseQuery(lodApiInstance);

export const lodApi = createApi({
  reducerPath: "lodApi",
  baseQuery,
  tagTypes: [
    "Order",
    "OrderStatus",
    "Journey",
    "JourneyTransactions",
    "TrackPersistences",
    "JourneyProximities",
    "ProximityTimeRanges",
  ],
  endpoints: (builder) => ({
    orders: builder.query<OrdersResponse, OrdersQueryParams>({
      query: (params) => ({
        url: `/orders`,
        method: "GET",
        params: {
          ...params,
          limit: 100000,
        },
      }),
      providesTags: ["Order"],
      keepUnusedDataFor: Infinity,
    }),

    orderById: builder.query<OrderWithJourneysResponse, string>({
      query: (orderId) => ({
        url: `/orders/${orderId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, orderId) => [
        { type: "Order", id: orderId },
      ],
    }),

    updateOrderStatus: builder.mutation<
      { success: true },
      {
        orderId: string;
        status: "not_started" | "finished" | "archived" | "pending";
      }
    >({
      query: ({ orderId, status }) => ({
        url: `/orders/${orderId}/update_status`,
        method: "POST",
        data: { status },
      }),
      invalidatesTags: ["OrderStatus"],
    }),

    createJourney: builder.mutation<
      { id: string },
      { orderId: string; journey: JourneyEventRequest }
    >({
      query: ({ orderId, journey }) => ({
        url: `/orders/${orderId}/journeys`,
        method: "POST",
        data: journey,
      }),
      invalidatesTags: ["Journey", "OrderStatus"],
    }),

    createJourneys: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      { orderId: string; journeys: JourneyEventRequest[] }
    >({
      async queryFn(
        { orderId, journeys },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const journey of journeys) {
          try {
            await fetchWithBQ({
              url: `/orders/${orderId}/journeys`,
              method: "POST",
              data: journey,
            });

            results.push({ success: true });
          } catch (error) {
            results.push({ error });
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["Journey", "OrderStatus"],
    }),

    updateJourneys: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      {
        orderId: string;
        journeys: Array<{
          id: string;
          journey: JourneyEventRequest;
        }>;
      }
    >({
      async queryFn(
        { orderId, journeys },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const item of journeys) {
          try {
            await fetchWithBQ({
              url: `/orders/${orderId}/journeys/${item.id}`,
              method: "PUT",
              data: item.journey,
            });

            results.push({ success: true });
          } catch (error) {
            results.push({ error });
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["Journey", "OrderStatus"],
    }),

    deleteJourneys: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      { orderId: string; journeyIds: string[] }
    >({
      async queryFn(
        { orderId, journeyIds },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const journeyId of journeyIds) {
          try {
            await fetchWithBQ({
              url: `/orders/${orderId}/journeys/${journeyId}`,
              method: "DELETE",
            });

            results.push({ success: true });
          } catch (error) {
            results.push({ error });
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["Journey", "OrderStatus"],
    }),

    createJourneyTransactions: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      {
        orderId: string;
        journeyAnnotations: Array<{
          journeyId: string;
          records: JourneyTransactionRequest[];
        }>;
      }
    >({
      async queryFn(
        { orderId, journeyAnnotations },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const journeyAnnotation of journeyAnnotations) {
          const { journeyId, records } = journeyAnnotation;

          for (const record of records) {
            try {
              await fetchWithBQ({
                url: `/orders/${orderId}/journeys/${journeyId}/transaction_annotations`,
                method: "POST",
                data: record,
              });

              results.push({ success: true });
            } catch (error) {
              results.push({ error });
            }
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["JourneyTransactions"],
    }),

    deleteJourneyTransactions: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      {
        orderId: string;
        journeyAnnotations: Array<{
          journeyId: string;
          records: JourneyTransactionResponse[];
        }>;
      }
    >({
      async queryFn(
        { orderId, journeyAnnotations },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const journeyAnnotation of journeyAnnotations) {
          const { journeyId, records } = journeyAnnotation;

          for (const record of records) {
            try {
              await fetchWithBQ({
                url: `/orders/${orderId}/journeys/${journeyId}/transaction_annotations/${record.id}`,
                method: "DELETE",
              });

              results.push({ success: true });
            } catch (error) {
              results.push({ error });
            }
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["JourneyTransactions"],
    }),

    createTrackPersistences: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      {
        orderId: string;
        journeyAnnotations: Array<{
          journeyId: string;
          records: TrackPersistenceRequest[];
        }>;
      }
    >({
      async queryFn(
        { orderId, journeyAnnotations },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const journeyAnnotation of journeyAnnotations) {
          const { journeyId, records } = journeyAnnotation;

          for (const record of records) {
            try {
              await fetchWithBQ({
                url: `/orders/${orderId}/journeys/${journeyId}/track_persistence_annotations`,
                method: "POST",
                data: record,
              });

              results.push({ success: true });
            } catch (error) {
              results.push({ error });
            }
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["TrackPersistences"],
    }),

    deleteTrackPersistences: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      {
        orderId: string;
        journeyAnnotations: Array<{
          journeyId: string;
          records: TrackPersistenceResponse[];
        }>;
      }
    >({
      async queryFn(
        { orderId, journeyAnnotations },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const journeyAnnotation of journeyAnnotations) {
          const { journeyId, records } = journeyAnnotation;

          for (const record of records) {
            try {
              await fetchWithBQ({
                url: `/orders/${orderId}/journeys/${journeyId}/track_persistence_annotations/${record.id}`,
                method: "DELETE",
              });

              results.push({ success: true });
            } catch (error) {
              results.push({ error });
            }
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["TrackPersistences"],
    }),

    createJourneyProximities: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      {
        orderId: string;
        journeyAnnotations: Array<{
          journeyId: string;
          records: JourneyProximityRequest[];
        }>;
      }
    >({
      async queryFn(
        { orderId, journeyAnnotations },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const journeyAnnotation of journeyAnnotations) {
          const { journeyId, records } = journeyAnnotation;

          for (const record of records) {
            try {
              await fetchWithBQ({
                url: `/orders/${orderId}/journeys/${journeyId}/proximity_annotations`,
                method: "POST",
                data: record,
              });

              results.push({ success: true });
            } catch (error) {
              results.push({ error });
            }
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["JourneyProximities"],
    }),

    deleteJourneyProximities: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      {
        orderId: string;
        journeyAnnotations: Array<{
          journeyId: string;
          records: JourneyProximityResponse[];
        }>;
      }
    >({
      async queryFn(
        { orderId, journeyAnnotations },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const journeyAnnotation of journeyAnnotations) {
          const { journeyId, records } = journeyAnnotation;

          for (const record of records) {
            try {
              await fetchWithBQ({
                url: `/orders/${orderId}/journeys/${journeyId}/proximity_annotations/${record.id}`,
                method: "DELETE",
              });

              results.push({ success: true });
            } catch (error) {
              results.push({ error });
            }
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["JourneyProximities"],
    }),

    createProximityTimeRanges: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      {
        orderId: string;
        journeyAnnotations: Array<{
          journeyId: string;
          records: ProximityTimeRangeRequest[];
        }>;
      }
    >({
      async queryFn(
        { orderId, journeyAnnotations },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const journeyAnnotation of journeyAnnotations) {
          const { journeyId, records } = journeyAnnotation;

          for (const record of records) {
            const {
              proximity_annotation_id: proximityAnnotationId,
              ...recordData
            } = record;
            try {
              await fetchWithBQ({
                url: `/orders/${orderId}/journeys/${journeyId}/proximity_annotations/${proximityAnnotationId}/proximity_range_annotations`,
                method: "POST",
                data: recordData,
              });

              results.push({ success: true });
            } catch (error) {
              results.push({ error });
            }
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["ProximityTimeRanges"],
    }),

    deleteProximityTimeRanges: builder.mutation<
      { results: Array<{ success: boolean } | { error: any }> },
      {
        orderId: string;
        journeyAnnotations: Array<{
          journeyId: string;
          records: ProximityTimeRangeResponse[];
        }>;
      }
    >({
      async queryFn(
        { orderId, journeyAnnotations },
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const results = [];

        for (const journeyAnnotation of journeyAnnotations) {
          const { journeyId, records } = journeyAnnotation;
          for (const record of records) {
            try {
              await fetchWithBQ({
                url: `/orders/${orderId}/journeys/${journeyId}/proximity_annotations/${record.proximity_annotation_id}/proximity_range_annotations/${record.id}`,
                method: "DELETE",
              });

              results.push({ success: true });
            } catch (error) {
              results.push({ error });
            }
          }
        }

        return { data: { results } };
      },
      invalidatesTags: ["ProximityTimeRanges"],
    }),
    getExportCounts: builder.query<
      ExportCountsResponse,
      ExportCountsQueryParams
    >({
      query: (params) => ({
        url: `/orders/export/count`,
        method: "GET",
        params,
      }),
    }),
  }),
});

export const {
  useOrdersQuery,
  useLazyOrderByIdQuery,
  useUpdateOrderStatusMutation,
  useCreateJourneysMutation,
  useUpdateJourneysMutation,
  useDeleteJourneysMutation,
  useCreateJourneyTransactionsMutation,
  useDeleteJourneyTransactionsMutation,
  useCreateTrackPersistencesMutation,
  useDeleteTrackPersistencesMutation,
  useCreateJourneyProximitiesMutation,
  useDeleteJourneyProximitiesMutation,
  useCreateProximityTimeRangesMutation,
  useDeleteProximityTimeRangesMutation,
  useLazyGetExportCountsQuery,
} = lodApi;

export type OrdersQueryParams = {
  search?: string;
  page?: number;
  limit?: number;
  ordering?: string;
};

export type OrdersResponse = {
  results: Order[];
  count: number;
};

export type OrderWithJourneysResponse = {
  result: OrderWithJourneys;
};

export type Order = {
  id: string;
  time: string;
  order_source: string;
  lines: {
    barcode: string;
    image_url: string;
    name: string;
    quantity: number;
    sku_base_id: number;
    variant_id: string;
  }[];
  annotator_id: string;
  status: string;
};

export type JourneyEventRequest = {
  enter_camera_id: string;
  enter_pin_xy: [number, number];
  enter_time: string;
  enter_zone: {
    zone_2d_polygon: number[];
    zone_3d_polygon: number[];
    zone_id: string;
    zone_name: string;
  };
  exit_camera_id: string;
  exit_pin_xy: number[];
  exit_time: string;
  exit_zone: {
    zone_2d_polygon: number[];
    zone_3d_polygon: number[];
    zone_id: string;
    zone_name: string;
  };
  note: string;
  enter_note: string;
  exit_note: string;
  enter_camera_image_width: number;
  enter_camera_image_height: number;
  exit_camera_image_width: number;
  exit_camera_image_height: number;
};

export type JourneyEventResponse = {
  id: string;
} & JourneyEventRequest;

export type JourneyTransaction = {
  id: string;
  type: typeof ANNOTATION_TYPES.TRANSACTION;
  status: AnnotationRecordStatus;
  orderId: string;
  journeyId: string;
  timestamp: string;
  name: string;
  barcode: string;
  imageUrl: string;
  skuId: number;
  variantId: string;
  count: number;
  action: CustomerAction;
  gondolaId: number;
  gondolaName: string;
  planogramId: number;
  cameraImageHeight: number;
  cameraImageWidth: number;
  pinCoordinates: RelativeCoordinate;
  cameraId: number;
  calibrationId: number;
  notes: string;
};

export type JourneyTransactionRequest = {
  closest_time_point: string;

  barcode: string;
  image_url: string;
  name: string;
  sku_base_id: number;
  variant_id: string;

  count: number;
  update_type: "add" | "remove";
  gondola_id: number;
  gondola_name: string;
  planogram_id: number;

  camera_image_height: number;
  camera_image_width: number;
  item_pin_2d_xy: [number, number];

  camera_local_id: number;
  calibration_id: number;

  notes: string;
};

export type JourneyTransactionResponse = {
  id: string;
} & JourneyTransactionRequest;

export type TrackPersistence = {
  id: string;
  type: typeof ANNOTATION_TYPES.TRACK_PERSISTENCE;
  status: AnnotationRecordStatus;
  orderId: string;
  journeyId: string;
  timestamp: string;
  cameraImageHeight: number;
  cameraImageWidth: number;
  pinCoordinates: RelativeCoordinate;
  cameraId: number;
  calibrationId: number;
  notes: string;
};

export type TrackPersistenceRequest = {
  closest_time_point: string;
  camera_image_height: number;
  camera_image_width: number;
  shoulder_center2d_xy: [number, number];
  camera_local_id: number;
  calibration_id: number;
  notes: string;
};

export type TrackPersistenceResponse = {
  id: string;
} & TrackPersistenceRequest;

export type JourneyProximity = {
  id: string;
  type: typeof ANNOTATION_TYPES.PROXIMITY;
  status: AnnotationRecordStatus;
  orderId: string;
  journeyId: string;
  handId: HandId;
  timestamp: string;
  cameraImageHeight: number;
  cameraImageWidth: number;
  pinCoordinates: RelativeCoordinate;
  cameraId: number;
  calibrationId: number;
  gondolaId: number;
  gondolaName: string;
  planogramId: number;
  notes: string;
};

export type JourneyProximityRequest = {
  closest_time_point: string;
  hand_id: number;
  camera_image_height: number;
  camera_image_width: number;
  wrist2d_xy: [number, number];
  camera_local_id: number;
  calibration_id: number;
  gondola_id: number;
  gondola_name: string;
  planogram_id: number;
  notes: string;
};

export type JourneyProximityResponse = {
  id: string;
} & JourneyProximityRequest;

export type ProximityTimeRange = {
  id: string;
  type: typeof ANNOTATION_TYPES.PROXIMITY_TIME_RANGES;
  status: AnnotationRecordStatus;
  orderId: string;
  journeyId: string;
  proximityId: string;
  handId: HandId;
  timestamp: string;
  cameraImageHeight: number;
  cameraImageWidth: number;
  pinCoordinates: RelativeCoordinate;
  gondolaId: number;
  gondolaName: string;
  planogramId: number;
  beforeRangeStartTime: string;
  beforeRangeEndTime: string;
  afterRangeStartTime: string;
  afterRangeEndTime: string;
  cameraId: number;
  calibrationId: number;
  notes: string;
};

export type ProximityTimeRangeRequest = {
  proximity_annotation_id: string;
  before_range_start_time: string;
  before_range_end_time: string;
  after_range_start_time: string;
  after_range_end_time: string;
  camera_local_id: number;
  calibration_id: number;
  notes: string;
};

export type ProximityTimeRangeResponse = {
  id: string;
} & ProximityTimeRangeRequest;

export interface ExportCountsQueryParams {
  from_datetime?: string;
  to_datetime?: string;
  order_source?: string;
  search?: string;
}

export interface ExportCountsResponse {
  result: {
    orders_count: number;
    journeys_count: number;
    transaction_annotations_count: number;
    proximity_annotations_count: number;
    track_persistence_annotations_count: number;
  };
}

export interface ExportAnnotationsRequest {
  from_datetime?: string;
  to_datetime?: string;
  order_source?: string;
  search?: string;
  orders: boolean;
  journeys: boolean;
  transaction_annotations: boolean;
  proximity_annotations: boolean;
  track_persistence_annotations: boolean;
}
